import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge, Card, Input, Spinner, Table, Td, Th } from '@/components/admin/ui';
import {
  EmptyState,
  KpiTile,
  Pagination,
  UserLink,
} from '@/components';
import type { DonationRedemptionsResponse } from '@/lib/admin-types';
import { formatAdminDateTime, formatInt, formatNumber } from '@/lib/format';
import { apiJson } from '@/lib/api';

type SortKey = 'granted_at_desc' | 'granted_at_asc';

const BUCKET_LABEL: Record<string, string> = {
  '1yr': '1 year free',
  '6mo': '6 months free',
  '3mo': '3 months free',
};

function bucketLabel(bucket: string): string {
  return BUCKET_LABEL[bucket] ?? bucket;
}

function bucketBadgeVariant(bucket: string): 'primary' | 'success' | 'neutral' {
  if (bucket === '1yr') return 'success';
  if (bucket === '6mo') return 'primary';
  return 'neutral';
}

function DonationsPage() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [bucket, setBucket] = useState('');
  const [grantedOnly, setGrantedOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('granted_at_desc');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'donations', qDebounced, bucket, grantedOnly, sort, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      if (bucket) params.set('bucket', bucket);
      if (grantedOnly) params.set('granted_only', 'true');
      params.set('sort', sort);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      return apiJson<DonationRedemptionsResponse>(
        `/v1/admin/donation-redemptions?${params.toString()}`
      );
    },
  });

  const summary = data?.summary;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Donations</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Past one-time donors converted to free subscription time (RevenueCat promotional
          entitlements).
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiTile
          label="Rewards granted"
          value={summary?.granted_count ?? 0}
          loading={!summary}
          tooltip="Redemption rows where free time was granted (granted_days > 0)."
        />
        <KpiTile
          label="Distinct donors"
          value={summary?.distinct_donors ?? 0}
          loading={!summary}
          tooltip="Unique RevenueCat app_user_ids that redeemed."
        />
        <KpiTile
          label="Signed-in donors"
          value={summary?.signed_in_donors ?? 0}
          loading={!summary}
          tooltip="Donors linked to a Clerk account (durable across reinstalls)."
        />
        <KpiTile
          label="Anonymous donors"
          value={summary?.anonymous_donors ?? 0}
          loading={!summary}
          tooltip="Donors with no linked account yet."
        />
        <KpiTile
          label="Total donated"
          value={summary ? `$${formatNumber(summary.total_value_usd, { maxFractionDigits: 2 })}` : '—'}
          loading={!summary}
          tooltip="Sum of recorded donation USD value across redemptions."
        />
      </div>

      {summary && (
        <div className="flex flex-wrap gap-2">
          {(['1yr', '6mo', '3mo'] as const).map((b) => (
            <Badge key={b} variant={bucketBadgeVariant(b)}>
              {bucketLabel(b)}: {formatInt(summary.by_bucket[b] ?? 0)}
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <Input
              label="Search (app_user_id, product, transaction id, email, name)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="type to filter"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="mb-2 block text-sm text-nova-muted-foreground">Bucket</label>
            <select
              className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
              value={bucket}
              onChange={(e) => {
                setBucket(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="1yr">1 year</option>
              <option value="6mo">6 months</option>
              <option value="3mo">3 months</option>
            </select>
          </div>
          <div className="w-full sm:w-44">
            <label className="mb-2 block text-sm text-nova-muted-foreground">Sort</label>
            <select
              className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
            >
              <option value="granted_at_desc">Newest first</option>
              <option value="granted_at_asc">Oldest first</option>
            </select>
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-nova-muted-foreground">
            <input
              type="checkbox"
              checked={grantedOnly}
              onChange={(e) => {
                setGrantedOnly(e.target.checked);
                setPage(1);
              }}
            />
            Granted only
          </label>
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
        {isError && (
          <p className="text-sm text-red-600">
            {(error as Error).message}
            <button
              type="button"
              className="ml-2 underline"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </button>
          </p>
        )}
        {data && data.items.length === 0 && <EmptyState title="No donation redemptions match" />}
        {data && data.items.length > 0 && (
          <>
            <div className="max-w-full overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Granted</Th>
                    <Th>Donor</Th>
                    <Th>Reward</Th>
                    <Th>Value</Th>
                    <Th>Product</Th>
                    <Th>Expires</Th>
                    <Th>Transaction</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={row.id}>
                      <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                        {formatAdminDateTime(row.granted_at)}
                      </Td>
                      <Td>
                        {row.user_id ? (
                          <UserLink
                            userId={row.user_id}
                            displayName={row.user_display_name}
                            email={row.user_email}
                          >
                            {row.user_display_name ?? row.user_email ?? row.app_user_id.slice(0, 12)}
                          </UserLink>
                        ) : (
                          <span className="font-mono text-xs text-nova-muted-foreground">
                            {row.app_user_id.slice(0, 16)}
                          </span>
                        )}
                        <span className="ml-1 align-middle">
                          {row.user_id ? (
                            <Badge variant="primary">account</Badge>
                          ) : (
                            <Badge variant="neutral">anon</Badge>
                          )}
                        </span>
                        {row.user_email ? (
                          <span className="block truncate text-xs text-nova-muted-foreground">
                            {row.user_email}
                          </span>
                        ) : null}
                      </Td>
                      <Td>
                        {row.granted ? (
                          <Badge variant={bucketBadgeVariant(row.bucket)}>
                            {bucketLabel(row.bucket)}
                          </Badge>
                        ) : (
                          <Badge variant="neutral">no-op</Badge>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap text-nova-muted-foreground">
                        ${formatNumber(row.value_usd, { maxFractionDigits: 2 })}
                      </Td>
                      <Td className="max-w-[12rem] truncate font-mono text-xs text-nova-muted-foreground">
                        {row.product_id}
                      </Td>
                      <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                        {row.expires_at ? formatAdminDateTime(row.expires_at) : '—'}
                      </Td>
                      <Td className="max-w-[10rem] truncate font-mono text-xs text-nova-muted-foreground">
                        {row.rc_transaction_id}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Pagination
              page={data.page}
              pageSize={data.page_size}
              total={data.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/donations')({ component: DonationsPage });
